import { useParams, useNavigate } from "react-router-dom";
import { useProduct } from "@/hooks/useProduct";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, ShoppingCart, Heart, Share2, MapPin, Star, ExternalLink, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import AnimatedBackground from "@/components/ui/animated-background";
import { Link } from "react-router-dom";
import { StoreIcon } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import { TruncatedText } from "@/components/ui/TruncatedText";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * ProductDetail Component
 * Displays a product's details including variants, images, and store information
 * Handles variant selection and URL state management
 */
export default function ProductDetail() {
  // Hooks and state
  const { id, variantId } = useParams();
  const navigate = useNavigate();
  
  console.log("ProductDetail - Route params:", { id, variantId });
  
  const { data: product, isLoading, error } = useProduct({ 
    productId: id || "", 
    variantId: variantId 
  });
  
  const [selectedVariantId, setSelectedVariantId] = useState<string>();
  const { addItem } = useCart();
  const [loading, setLoading] = useState(false);

  // Log product data when it changes
  useEffect(() => {
    if (product) {
      console.log("ProductDetail - Product data loaded:", { 
        id: product.id,
        title: product.title,
        variants: product.variants.map(v => ({ id: v.id, size: v.size })),
        selectedVariant: product.selectedVariant
      });
    }
  }, [product]);

  // Handle initial variant selection and URL sync
  useEffect(() => {
    if (!product?.variants?.length) {
      // console.log("ProductDetail - No variants available");
      return;
    }
    
    const variant = variantId 
      ? product.variants.find(v => v.id === variantId)
      : product.variants[0];
    
    // console.log("ProductDetail - Variant selection:", { 
    //   variantId, 
    //   foundVariant: variant ? variant.id : 'none',
    //   availableVariants: product.variants.map(v => v.id)
    // });
    
    if (variant) {
      setSelectedVariantId(variant.id);
      if (!variantId) {
        // Only navigate if we're not already on a variant URL
        // console.log("ProductDetail - Redirecting to variant URL:", `/product/${id}/${variant.id}`);
        navigate(`/product/${id}/${variant.id}`, { replace: true });
      }
    }
  }, [product, variantId, id, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-accent">
        <div className="max-w-[85%] mx-auto pt-20 pb-12 px-6">
          <div className="max-w-[1200px] mx-auto">
            <Skeleton className="h-5 w-64 mb-6" />
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <Skeleton className="aspect-[4/5] w-full rounded-lg" />
                <div className="space-y-4">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-6 w-24" />
                  <div className="flex gap-3 mt-6">
                    <Skeleton className="h-10 w-24 rounded-full" />
                    <Skeleton className="h-10 w-24 rounded-full" />
                    <Skeleton className="h-10 w-24 rounded-full" />
                  </div>
                  <Skeleton className="h-20 w-full mt-4" />
                  <Skeleton className="h-12 w-full rounded-lg mt-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    console.error("ProductDetail - Error or no product:", error);
    return (
      <div className="min-h-screen bg-brand-background py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-xl p-8 shadow-sm">
            <h2 className="text-2xl font-semibold mb-4 text-brand-secondary">Product Not Found</h2>
            <p className="text-brand-secondary/70 mb-8">The product you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate(-1)} className="bg-[#FDF5F5] text-brand-secondary hover:bg-[#FDF5F5]/90">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get the selected variant or default to the first one
  const selectedVariant = product.variants.find(v => v.id === selectedVariantId) || product.variants[0];
  console.log("ProductDetail - Selected variant:", selectedVariant?.id);

  // Handle variant selection
  const handleVariantChange = (value: string) => {
    console.log("ProductDetail - Changing variant to:", value);
    setSelectedVariantId(value);
    navigate(`/product/${id}/${value}`, { replace: true });
  };
  
  // Handle add to cart
  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.error("Selection required", {
        description: "Please select a size before adding to cart"
      });
      return;
    }
    
    try {
      console.log("Product data:", {
        id: product.id,
        store: product.store,
        store_id: product.store_id
      });
      
      const cartItem = {
        id: selectedVariant.id,
        title: product.title,
        price: selectedVariant.price || 0,
        image: selectedVariant.main_image_url || product.main_image_url || undefined,
        floristId: product.store_id,  // Use store_id from the product table
        floristName: product.store?.store_name,
        size: selectedVariant.size,
        productId: product.id,
        sizeId: selectedVariant.id
      };
      
      console.log("Adding to cart:", cartItem);
      
      // Add to cart
      addItem(cartItem);
      
      // Show success toast
      toast.success("Added to Cart", {
        description: `${product.title} (${selectedVariant.size}) has been added to your cart`,
        action: {
          label: "View Cart",
          onClick: () => navigate("/cart")
        },
        className: "border-brand-primary/20 bg-white text-[#4A4F41]"
      });
      
      // Visual feedback
      const button = document.getElementById('add-to-cart-button');
      if (button) {
        button.classList.add('bg-green-500');
        setTimeout(() => {
          button.classList.remove('bg-green-500');
        }, 1000);
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error("Error", {
        description: "Could not add item to cart. Please try again.",
        className: "border-brand-primary/20 bg-white text-[#4A4F41]"
      });
    }
  };
  
  /**
   * IMPORTANT: Image Naming Conventions
   * 
   * These variables provide semantic names based on how images are used in the UI,
   * regardless of the database field names. This makes the code more maintainable:
   * 
   * - profileSquareImage: The square image displayed at the top of the florist section
   *   (Currently mapped to store.storefront_image_url in the database)
   * 
   * - storefrontImage: The wide landscape image at the bottom of the florist section
   *   (Currently mapped to store.cover_image_url in the database)
   * 
   * When modifying image display, refer to these semantic variables rather than 
   * directly using the database field names to avoid confusion.
   */
  const profileSquareImage = product.store?.storefront_image_url;
  const storefrontImage = product.store?.cover_image_url;

  return (
    <div className="min-h-screen bg-brand-accent">
      {/* Reduced top padding since navigation is now at bottom */}
      <div className="max-w-[95%] sm:max-w-[85%] mx-auto pt-6 sm:pt-20 pb-16 sm:pb-12 px-2 sm:px-6">
        <div className="max-w-[1200px] mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-3 sm:mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {product.store && (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild><Link to={`/florist/${product.store.id}`}>{product.store.store_name || product.store.name}</Link></BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbPage>{product.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Main Content */}
          <div className="bg-white rounded-xl p-3 sm:p-8 shadow-sm border border-brand-primary/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10">
              {/* Left Column - Image */}
              <div>
                {/* Removing Mobile product actions - heart and share buttons */}
                
                <div className="aspect-[4/5] relative rounded-lg overflow-hidden bg-brand-accent">
                  <img
                    src={selectedVariant?.main_image_url || product.main_image_url || "/images/placeholder.jpg"}
                    alt={product.title}
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>

              {/* Right Column - Product Info */}
              <div className="font-suisse">
                {/* Title and Store */}
                <div className="mb-4 sm:mb-8">
                  <h1 className="text-[24px] sm:text-[32px] leading-[1.2] text-brand-primary font-medium mb-2">
                    {product.title}
                  </h1>
                  <div className="flex items-center justify-between">
                    <p className="text-[15px] text-brand-primary/70">
                      By {product.store?.store_name}
                    </p>
                    {/* Removing Desktop only action buttons */}
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4 sm:mb-8">
                  <TruncatedText 
                    text={product.description} 
                    charLimit={120}
                    textClassName="text-[15px] text-brand-primary/80 leading-relaxed"
                    buttonClassName="text-brand-primary hover:text-brand-primary/80 md:hidden"
                  />
                </div>

                {/* Variants Selection */}
                {product.variants.length > 0 && (
                  <div className="mb-4 sm:mb-8">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div>
                        <h3 className="text-[17px] font-medium text-brand-primary">
                          ${selectedVariant?.price}
                        </h3>
                      </div>
                      <div className="text-right">
                        <p className="text-[15px] text-brand-primary">
                          {selectedVariant?.size}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-2">
                      {product.variants.map((variant) => (
                        <div 
                          key={variant.id}
                          onClick={() => handleVariantChange(variant.id)}
                          className={`w-full px-4 py-3 rounded-lg cursor-pointer transition-all ${
                            selectedVariant?.id === variant.id 
                              ? 'bg-brand-accent border border-brand-primary' 
                              : 'border border-transparent hover:border-[#843329] hover:bg-[#843329]/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[15px] text-brand-primary">{variant.size}</span>
                            <span className="text-[15px] text-brand-primary">${variant.price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add to Cart */}
                <Button 
                  id="add-to-cart-button"
                  className="w-full h-12 bg-[#843329] hover:bg-[#967F81] text-white text-[15px] font-suisse transition-colors duration-300"
                  onClick={handleAddToCart}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Enhanced Store Info Section - Full Width */}
            {product.store && (
              <div className="mt-6 sm:mt-10 pt-6 sm:pt-10 border-t border-brand-primary/10">
                {/* Mobile: No container border for consistent margins */}
                <div className="md:rounded-lg md:overflow-hidden md:shadow-sm md:hover:shadow md:transition-all md:duration-300 md:border md:border-brand-primary/10 bg-white">
                  {/* Florist Header with Name */}
                  <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-5">
                    {/* Store Name and Location */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-[18px] sm:text-[22px] font-medium text-brand-primary">
                          {product.store.store_name}
                        </h3>
                        <Link 
                          to={`/florist/${encodeURIComponent(product.store.id)}`}
                          className="text-[11px] sm:text-[12px] text-brand-primary/70 hover:text-brand-primary px-2 sm:px-3 py-1 border border-brand-primary/20 rounded-full hover:bg-[#843329]/50 transition-colors"
                        >
                          View Store
                        </Link>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                        {product.store.suburb && product.store.state && (
                          <div className="inline-flex items-center text-[13px] sm:text-[14px] text-brand-primary/80">
                            <MapPin className="h-3.5 sm:h-4 w-3.5 sm:w-4 mr-1 sm:mr-1.5" />
                            {product.store.suburb}, {product.store.state}
                          </div>
                        )}
                        
                        {/* Delivery Radius Badge */}
                        {product.store.delivery_radius && (
                          <div className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 rounded-full bg-brand-accent text-[12px] sm:text-[13px] text-brand-primary whitespace-nowrap">
                            <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5 mr-1 sm:mr-1.5" />
                            {product.store.delivery_radius}km Radius
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Image Layout - Side by Side on desktop, stacked on mobile */}
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <div className="flex flex-col md:flex-row h-auto md:h-[400px] overflow-hidden rounded-xl shadow-sm md:gap-6 bg-brand-accent/30">
                      {/* Square Profile Image - 1/3 width on desktop but maintains square aspect ratio on all devices */}
                      <div className="w-full md:w-1/3 p-3">
                        <div className="aspect-square w-full relative rounded-lg overflow-hidden">
                          {profileSquareImage ? (
                            <img 
                              src={profileSquareImage} 
                              alt={`${product.store.store_name} profile`}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#967F81]/10 flex items-center justify-center">
                              <StoreIcon className="w-12 sm:w-16 h-12 sm:h-16 text-brand-primary/50" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Landscape Storefront Image - full width on mobile, 2/3 width on desktop */}
                      <div className="w-full md:w-2/3 h-[160px] sm:h-[200px] md:h-full p-3">
                        {storefrontImage ? (
                          <div className="w-full h-full relative rounded-lg overflow-hidden">
                            <img 
                              src={storefrontImage} 
                              alt={`${product.store.store_name} storefront`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-l from-black/30 to-transparent"></div>
                          </div>
                        ) : (
                          <div className="w-full h-full bg-[#967F81]/10 flex items-center justify-center rounded-lg">
                            <StoreIcon className="w-16 sm:w-20 h-16 sm:h-20 text-brand-primary/30" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Section with Delivery Info and About Store - stacked on mobile, side by side on desktop */}
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {/* Delivery Information - Takes full width on mobile, 1/3 on desktop */}
                    <div className="md:col-span-1">
                      <h4 className="text-[14px] sm:text-[15px] font-medium text-brand-primary mb-2 sm:mb-3">Delivery Information</h4>
                      <div className="flex flex-col gap-2">
                        {product.store.same_day_enabled && (
                          <div className="inline-flex items-center px-3 py-2 rounded-full bg-[#967F81]/10 text-[12px] sm:text-[13px] text-brand-primary">
                            <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5 mr-1 sm:mr-1.5" />
                            Same-Day Delivery Available
                          </div>
                        )}
                        
                        {product.store.delivery_cutoff_times && 
                        Object.keys(product.store.delivery_cutoff_times).length > 0 && (
                          <div className="inline-flex items-center px-3 py-2 rounded-full bg-[#967F81]/10 text-[12px] sm:text-[13px] text-brand-primary">
                            <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5 mr-1 sm:mr-1.5" />
                            Same-Day Cutoff: {Object.values(product.store.delivery_cutoff_times)[0]}
                          </div>
                        )}
                        
                        {product.store.delivery_days && product.store.delivery_days.length > 0 && (
                          <div className="inline-flex items-center px-3 py-2 rounded-full bg-[#967F81]/10 text-[12px] sm:text-[13px] text-brand-primary">
                            <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5 mr-1 sm:mr-1.5" />
                            Delivery Days: {product.store.delivery_days.map(day => 
                              day.charAt(0).toUpperCase() + day.slice(1)
                            ).join(", ")}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Store Description - Takes full width on mobile, 2/3 on desktop */}
                    {product.store.description && (
                      <div className="md:col-span-2 bg-brand-accent/30 rounded-lg p-3 sm:p-4">
                        <h4 className="text-[14px] sm:text-[15px] font-medium text-brand-primary mb-1 sm:mb-2">About This Store</h4>
                        <TruncatedText 
                          text={product.store.description} 
                          charLimit={100}
                          textClassName="text-[13px] sm:text-[14px] text-brand-primary/80 leading-relaxed"
                          buttonClassName="text-brand-primary hover:text-brand-primary/80 md:hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 